// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	CUSTOM_SOUND_FORMATS,
	CUSTOM_SOUND_MAX_DURATION_LABEL,
	CUSTOM_SOUND_MAX_SIZE_LABEL,
	PREMIUM_PRODUCT_NAME,
	PRODUCT_NAME,
} from '@app/features/app/config/I18nDisplayConstants';
import {LimitResolver} from '@app/features/app/utils/LimitResolverAdapter';
import {isLimitToggleEnabled} from '@app/features/app/utils/LimitUtils';
import Guilds from '@app/features/guild/state/Guilds';
import SelectedGuild from '@app/features/navigation/state/SelectedGuild';
import {
	DMS_ENTRANCE_SOUND_SCOPE,
	type EntranceSoundScope,
	GLOBAL_ENTRANCE_SOUND_SCOPE,
	GUILDS_ENTRANCE_SOUND_SCOPE,
	getEntranceSoundScopeId,
	parseEntranceSoundScopeId,
} from '@app/features/notification/utils/EntranceSoundScopes';
import * as PremiumModalCommands from '@app/features/premium/commands/PremiumModalCommands';
import {shouldShowPremiumFeatures} from '@app/features/premium/utils/PremiumUtils';
import {Button} from '@app/features/ui/button/Button';
import type {SelectOption} from '@app/features/ui/components/form/Select';
import {Select} from '@app/features/ui/components/form/Select';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import styles from '@app/features/user/components/modals/tabs/components/EntranceSoundSection.module.css';
import {useEntranceSound} from '@app/features/user/components/modals/tabs/hooks/useEntranceSound';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {CrownIcon, SpeakerHighIcon, TrashIcon, UploadIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useMemo, useState} from 'react';

interface EntranceSoundScopeOption extends SelectOption<string> {
	value: string;
	label: string;
	description: string;
	icon?: string | null;
}
export const EntranceSoundSection: React.FC = observer(() => {
	const hasVoiceEntranceSounds = useMemo(
		() =>
			isLimitToggleEnabled(
				{feature_voice_entrance_sounds: LimitResolver.resolve({key: 'feature_voice_entrance_sounds', fallback: 0})},
				'feature_voice_entrance_sounds',
			),
		[],
	);
	const {t} = useLingui();
	const currentGuildId = SelectedGuild.selectedGuildId;
	const guilds = Guilds.getGuilds();
	const [selectedScopeId, setSelectedScopeId] = useState(() =>
		getEntranceSoundScopeId(currentGuildId ? {kind: 'guild', guildId: currentGuildId} : GLOBAL_ENTRANCE_SOUND_SCOPE),
	);
	const selectedScope = useMemo(
		() => parseEntranceSoundScopeId(selectedScopeId) ?? GLOBAL_ENTRANCE_SOUND_SCOPE,
		[selectedScopeId],
	);
	const orderedGuilds = useMemo(() => {
		const visibleGuilds = [...guilds];
		return visibleGuilds.sort((left, right) => {
			if (left.id === currentGuildId) return -1;
			if (right.id === currentGuildId) return 1;
			return (left.name || '').localeCompare(right.name || '');
		});
	}, [currentGuildId, guilds]);
	const getScopeLabel = useCallback(
		(scope: EntranceSoundScope): string => {
			switch (scope.kind) {
				case 'global':
					return t`All Communities and DMs`;
				case 'guilds':
					return t`All Communities`;
				case 'dms':
					return t`Direct Messages`;
				case 'guild':
					return Guilds.getGuild(scope.guildId)?.name ?? t`Unknown Community`;
			}
		},
		[t],
	);
	const getScopeDescription = useCallback(
		(scope: EntranceSoundScope): string => {
			switch (scope.kind) {
				case 'global':
					return t`Used by default unless a community or DM sound overrides it.`;
				case 'guilds':
					return t`Used in communities that do not have their own sound.`;
				case 'dms':
					return t`Used for all direct-message calls.`;
				case 'guild':
					return t`Used only in this community.`;
			}
		},
		[t],
	);
	const scopeOptions = useMemo<Array<EntranceSoundScopeOption>>(
		() => [
			{
				value: getEntranceSoundScopeId(GLOBAL_ENTRANCE_SOUND_SCOPE),
				label: getScopeLabel(GLOBAL_ENTRANCE_SOUND_SCOPE),
				description: getScopeDescription(GLOBAL_ENTRANCE_SOUND_SCOPE),
			},
			{
				value: getEntranceSoundScopeId(GUILDS_ENTRANCE_SOUND_SCOPE),
				label: getScopeLabel(GUILDS_ENTRANCE_SOUND_SCOPE),
				description: getScopeDescription(GUILDS_ENTRANCE_SOUND_SCOPE),
			},
			{
				value: getEntranceSoundScopeId(DMS_ENTRANCE_SOUND_SCOPE),
				label: getScopeLabel(DMS_ENTRANCE_SOUND_SCOPE),
				description: getScopeDescription(DMS_ENTRANCE_SOUND_SCOPE),
			},
			...orderedGuilds.map((guild) => ({
				value: getEntranceSoundScopeId({kind: 'guild', guildId: guild.id}),
				label: guild.name || '',
				description: getScopeDescription({kind: 'guild', guildId: guild.id}),
				icon: guild.icon ?? null,
			})),
		],
		[getScopeDescription, getScopeLabel, orderedGuilds],
	);
	const renderScopeRow = useCallback((option: EntranceSoundScopeOption, compact: boolean) => {
		const isGuild = option.value.startsWith('guild:');
		const guildId = isGuild ? option.value.slice('guild:'.length) : null;
		const iconUrl =
			isGuild && guildId && option.icon ? AvatarUtils.getGuildIconURL({id: guildId, icon: option.icon}) : null;
		const initial = option.label.charAt(0).toUpperCase();
		return (
			<div className={clsx(styles.scopeOptionRow, compact && styles.scopeValueRow)}>
				{isGuild &&
					(iconUrl ? (
						<div className={styles.scopeAvatar} style={{backgroundImage: `url(${iconUrl})`}} aria-hidden />
					) : (
						<div className={styles.scopeAvatarPlaceholder} aria-hidden>
							{initial}
						</div>
					))}
				<div className={styles.scopeText}>
					<span className={styles.scopeLabel}>{option.label}</span>
					{!compact && <span className={styles.scopeDescription}>{option.description}</span>}
				</div>
			</div>
		);
	}, []);
	const renderScopeOption = useCallback(
		(option: EntranceSoundScopeOption) => renderScopeRow(option, false),
		[renderScopeRow],
	);
	const renderScopeValue = useCallback(
		(option: EntranceSoundScopeOption | null) => (option ? renderScopeRow(option, true) : null),
		[renderScopeRow],
	);
	const getScopeHint = useCallback(
		(scope: EntranceSoundScope): string => {
			switch (scope.kind) {
				case 'global':
					return t`Your fallback entrance sound for all communities and direct messages. More specific settings override it.`;
				case 'guilds':
					return t`Used in communities that do not have their own sound set.`;
				case 'dms':
					return t`Used in direct-message calls. If you do not set one, your global sound is used instead.`;
				case 'guild':
					return t`Used in this community. If you do not set one, ${PRODUCT_NAME} falls back to your community default, then your global default.`;
			}
		},
		[t],
	);
	const {
		entranceSound,
		activeEntranceSound,
		inheritedEntranceSound,
		inheritedFromScope,
		isPreviewing,
		remove,
		preview,
		openUploadDialog,
	} = useEntranceSound(selectedScope);
	const scopeHint = getScopeHint(selectedScope);
	const inheritedFromLabel = inheritedFromScope ? getScopeLabel(inheritedFromScope) : null;
	const hasDirectOverride = entranceSound != null;
	const hasInheritedSound = inheritedEntranceSound != null && inheritedFromLabel != null;
	const uploadButtonLabel = hasDirectOverride
		? t`Replace Entrance Sound`
		: hasInheritedSound
			? t`Set Sound Here`
			: t`Upload Entrance Sound`;
	return (
		<div>
			<div className={styles.label}>
				<Trans>Entrance Sound</Trans>
			</div>
			{!hasVoiceEntranceSounds ? (
				shouldShowPremiumFeatures() ? (
					<div className={styles.premiumCard}>
						<div className={styles.premiumCardHeader}>
							<CrownIcon weight="fill" size={18} className={styles.premiumCardIcon} />
							<span className={styles.premiumCardTitle}>
								<Trans>Custom Entrance Sounds with {PREMIUM_PRODUCT_NAME}</Trans>
							</span>
						</div>
						<p className={styles.premiumCardDescription}>
							<Trans>
								Upload a custom sound that plays automatically when you join a voice channel. Maximum duration:{' '}
								{CUSTOM_SOUND_MAX_DURATION_LABEL}. Supported formats: {CUSTOM_SOUND_FORMATS} (max{' '}
								{CUSTOM_SOUND_MAX_SIZE_LABEL}).
							</Trans>
						</p>
						<Button variant="secondary" small={true} onClick={() => PremiumModalCommands.open()}>
							<Trans>Get {PREMIUM_PRODUCT_NAME}</Trans>
						</Button>
					</div>
				) : (
					<p className={styles.premiumCardDescription}>
						<Trans>Custom entrance sounds are not enabled on this instance.</Trans>
					</p>
				)
			) : (
				<div className={styles.content}>
					<div className={styles.hint}>
						<Trans>
							Choose where your entrance sound should play. More specific settings override broader defaults. Maximum
							duration: {CUSTOM_SOUND_MAX_DURATION_LABEL}, {CUSTOM_SOUND_MAX_SIZE_LABEL}. Supported formats:{' '}
							{CUSTOM_SOUND_FORMATS}.
						</Trans>
					</div>
					<div className={styles.scopePicker}>
						<Select<string, false, EntranceSoundScopeOption>
							label={t`Applies To`}
							value={selectedScopeId}
							options={scopeOptions}
							onChange={setSelectedScopeId}
							renderOption={renderScopeOption}
							renderValue={renderScopeValue}
						/>
						<p className={styles.scopeHint}>{scopeHint}</p>
					</div>
					{activeEntranceSound ? (
						<div className={styles.soundCard}>
							<div className={styles.soundCardHeader}>
								<span
									className={clsx(
										styles.scopeBadge,
										hasDirectOverride ? styles.scopeBadgeDirect : styles.scopeBadgeInherited,
									)}
								>
									{hasDirectOverride ? t`Set here` : t`Inherited`}
								</span>
								{hasInheritedSound && inheritedFromLabel ? (
									<span className={styles.inheritedText}>{t`Inherited from ${inheritedFromLabel}`}</span>
								) : null}
							</div>
							<div className={styles.soundCardContent}>
								<div className={styles.soundCardMain}>
									<Tooltip text={t`Preview sound`}>
										<button
											type="button"
											onClick={preview}
											disabled={isPreviewing}
											className={styles.previewButton}
											aria-label={t`Preview sound`}
										>
											<SpeakerHighIcon size={20} weight="fill" className={styles.previewIcon} />
										</button>
									</Tooltip>
									<div className={styles.soundInfo}>
										<span className={styles.soundFileName}>{activeEntranceSound.fileName}</span>
										<span className={styles.soundDuration}>{activeEntranceSound.duration.toFixed(1)}s</span>
									</div>
								</div>
								{hasDirectOverride && (
									<Tooltip text={t`Remove entrance sound`}>
										<button
											type="button"
											onClick={remove}
											className={styles.deleteButton}
											aria-label={t`Remove entrance sound`}
										>
											<TrashIcon size={16} className={styles.deleteIcon} />
										</button>
									</Tooltip>
								)}
							</div>
						</div>
					) : (
						<div className={styles.emptyState}>
							<div className={styles.emptyStateTitle}>
								<Trans>No entrance sound set here</Trans>
							</div>
							<p className={styles.emptyStateDescription}>
								{selectedScope.kind === 'global' ? (
									<Trans>Upload a default sound to use anywhere you have not set a more specific sound.</Trans>
								) : (
									<Trans>
										This scope does not have its own sound yet, and there is no broader default to use instead.
									</Trans>
								)}
							</p>
						</div>
					)}
					<div className={styles.actions}>
						<Button variant="primary" fitContainer={false} className={styles.actionButton} onClick={openUploadDialog}>
							<div className={styles.uploadButtonContent}>
								<UploadIcon size={16} />
								<span>{uploadButtonLabel}</span>
							</div>
						</Button>
					</div>
				</div>
			)}
		</div>
	);
});
